import { useQuery } from '@tanstack/react-query'
import { useProblemReportState } from '../../lib/problem-reporting/host'
import {
  assertReportRequest,
  reportQueryKey,
  reportRequestContext,
  settleReportRequest,
  type ReportRequestContext,
} from '../../lib/problem-reporting/context'
export async function reportRead<T>(
  signal: AbortSignal,
  read: (options: RequestInit) => Promise<T>,
  bound?: ReportRequestContext,
): Promise<T> {
  const context = bound ?? reportRequestContext()
  assertReportRequest(context)
  const result = await read({
    signal,
    headers: { 'X-Problem-Report-Context': context.identity.membershipId },
  })
  await settleReportRequest(context, signal)
  return result
}
export function useReportRead<T>(
  key: readonly unknown[],
  read: (options: RequestInit) => Promise<T>,
) {
  const host = useProblemReportState()
  const context = host.identity
    ? { identity: host.identity, generation: host.generation }
    : undefined
  return useQuery({
    queryKey: reportQueryKey(...key),
    queryFn: ({ signal }) => reportRead(signal, read, context),
    enabled: Boolean(host.identity),
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })
}
export function safeReportImageUrl(
  value: string,
  environment = {
    origin: window.location.origin,
    development: import.meta.env.DEV,
  },
) {
  try {
    const origin = new URL(environment.origin)
    const url = new URL(value, origin)
    if (url.username || url.password) return null
    const loopback = (host: string) =>
      ['localhost', '127.0.0.1', '[::1]'].includes(host)
    const localDevelopmentStorage =
      environment.development &&
      loopback(origin.hostname) &&
      loopback(url.hostname)
    return url.protocol === 'https:' ||
      (url.protocol === 'http:' &&
        (url.origin === origin.origin || localDevelopmentStorage))
      ? url.href
      : null
  } catch {
    return null
  }
}
